import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import API_BASE from "../config/api";

/* ============================================================
   BLANK defaults — a newly activated site shows its OWN (empty)
   content, never demo data. Real data (get_personal_site.php)
   overrides per-field.
   ============================================================ */
const DEMO = {
    name: "",
    currentjob: "",
    slogan: "",
    aboutme: "",
    experience: "",
    languages: "",
    mobile: "",
    whatsapp: "",
    email: "",
    addresses: "",
    aboutimg: "",
    coverimg: "",
    images: [],
};

/* What I Do / Expertise cards — filled by the owner (blank by default) */
const SERVICES = [];

/* Testimonials — blank by default */
const TESTIMONIALS = [];

/* Load CDN assets (Bootstrap 5, Bootstrap Icons, AOS) once, clean up on unmount */
function useCdnAssets() {
    useEffect(() => {
        const added = [];
        const addLink = (href) => {
            if (document.querySelector(`link[href="${href}"]`)) return;
            const l = document.createElement("link");
            l.rel = "stylesheet";
            l.href = href;
            l.dataset.pw = "1";
            document.head.appendChild(l);
            added.push(l);
        };
        const addScript = (src) =>
            new Promise((resolve) => {
                if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
                const s = document.createElement("script");
                s.src = src;
                s.async = true;
                s.dataset.pw = "1";
                s.onload = () => resolve();
                document.body.appendChild(s);
                added.push(s);
            });

        addLink("https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css");
        addLink("https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css");
        addLink("https://unpkg.com/aos@2.3.1/dist/aos.css");
        addLink("https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600;800&family=Poppins:wght@600;700;800&display=swap");

        (async () => {
            await addScript("https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/js/bootstrap.bundle.min.js");
            await addScript("https://unpkg.com/aos@2.3.1/dist/aos.js");
            if (window.AOS) window.AOS.init({ once: true, offset: 50 });
        })();

        return () => { added.forEach((n) => { try { n.remove(); } catch (e) { /* noop */ } }); };
    }, []);
}

export default function PersonalWebsite() {
    const { domain } = useParams();
    const [real, setReal] = useState(null);
    const [realImages, setRealImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [imgOk, setImgOk] = useState(true);

    // contact form
    const [cName, setCName] = useState("");
    const [cPhone, setCPhone] = useState("");
    const [cEmail, setCEmail] = useState("");
    const [cMsg, setCMsg] = useState("");

    useCdnAssets();

    useEffect(() => {
        if (!domain) { setLoading(false); return; }
        fetch(`${API_BASE}/api/get_personal_site.php?domain=${encodeURIComponent(domain)}`)
            .then((r) => r.json())
            .then((res) => {
                if (res.success && res.data) setReal(res.data);
                if (res.images && res.images.length) setRealImages(res.images);
            })
            .catch(() => { })
            .finally(() => setLoading(false));
    }, [domain]);

    // Fix blank-on-refresh: (re)init AOS once real content is rendered.
    // If AOS never loads, force-reveal so the page is never blank.
    useEffect(() => {
        if (loading) return;
        let tries = 0;
        const timer = setInterval(() => {
            if (window.AOS) {
                window.AOS.init({ once: true, offset: 50 });
                window.AOS.refresh();
                clearInterval(timer);
            } else if (++tries > 40) {
                document.querySelectorAll(".pw [data-aos]").forEach((el) => {
                    el.style.opacity = "1";
                    el.style.transform = "none";
                });
                clearInterval(timer);
            }
        }, 100);
        return () => clearInterval(timer);
    }, [loading]);

    const pick = (k) => (real && real[k] && String(real[k]).trim() !== "" ? real[k] : DEMO[k]);
    const d = {
        name: pick("name"),
        currentjob: pick("currentjob"),
        slogan: pick("slogan"),
        aboutme: pick("aboutme"),
        experience: pick("experience"),
        languages: pick("languages"),
        mobile: pick("mobile"),
        whatsapp: pick("whatsapp"),
        email: pick("email"),
        addresses: pick("addresses"),
        aboutimg: real ? (real.aboutimg || "") : DEMO.aboutimg,
        coverimg: real ? (real.coverimg || "") : DEMO.coverimg,
    };
    const images = realImages.length ? realImages : DEMO.images;

    const imgUrl = (u) => (!u ? "" : /^https?:\/\//i.test(u) ? u : `${API_BASE}/${String(u).replace(/^\/+/, "")}`);
    const photo = imgUrl(d.aboutimg);
    const cover = imgUrl(d.coverimg);
    const hasPhoto = photo && imgOk;
    const initials = (d.name || "DN").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
    const firstName = (d.name || "").trim().split(/\s+/)[0] || d.name;
    const langCount = d.languages ? d.languages.split(",").filter(Boolean).length : 0;

    const handleContact = (e) => {
        e.preventDefault();
        const subject = encodeURIComponent(`Website enquiry from ${cName || "Visitor"}`);
        const body = encodeURIComponent(`Name: ${cName}
Phone: ${cPhone}
Email: ${cEmail}

${cMsg}`);
        window.location.href = `mailto:${d.email}?subject=${subject}&body=${body}`;
    };

    if (loading) {
        return (
            <div className="pw-loading">
                <div className="spinner-border text-primary" role="status" />
            </div>
        );
    }

    return (
        <div className="pw" data-bs-spy="scroll" data-bs-target="#mainNav" data-bs-offset="80">

            {/* NAV */}
            <nav id="mainNav" className="navbar navbar-expand-lg navbar-light fixed-top shadow-sm py-3">
                <div className="container">
                    <a className="navbar-brand fw-bold fs-4 text-dark" href="#top">
                        <i className="bi bi-patch-check-fill text-primary me-2"></i>{d.name || "My Website"}
                    </a>
                    <button className="navbar-toggler border-0" type="button" data-bs-toggle="collapse" data-bs-target="#navbarContent">
                        <span className="navbar-toggler-icon"></span>
                    </button>
                    <div className="collapse navbar-collapse" id="navbarContent">
                        <ul className="navbar-nav mx-auto mb-2 mb-lg-0 fw-semibold">
                            <li className="nav-item"><a className="nav-link px-3" href="#portfolio">Portfolio</a></li>
                            <li className="nav-item"><a className="nav-link px-3" href="#about">About</a></li>
                            <li className="nav-item"><a className="nav-link px-3" href="#services">What I Do</a></li>
                            <li className="nav-item"><a className="nav-link px-3" href="#reviews">Reviews</a></li>
                        </ul>
                        <a href="#contact" className="btn btn-dark rounded-pill px-4 py-2 fw-semibold shadow-sm d-none d-lg-inline-block">Get in Touch</a>
                    </div>
                </div>
            </nav>

            {/* HERO — background image (coverimg) + framed profile photo (aboutimg) */}
            <header
                id="top"
                className="hero-section"
                style={cover ? { backgroundImage: `linear-gradient(to right, rgba(15,23,42,.94) 0%, rgba(15,23,42,.80) 50%, rgba(15,23,42,.62) 100%), url("${cover}")` } : undefined}
            >
                <div className="container">
                    <div className="row align-items-center g-5">
                        <div className="col-lg-7" data-aos="fade-up" data-aos-duration="800">
                            <div className="d-flex align-items-center mb-3 flex-wrap gap-2">
                                <span className="badge bg-success px-3 py-2 rounded-pill"><i className="bi bi-patch-check me-1"></i> Verified · Dial Nepal</span>
                                {d.currentjob && <span className="text-light opacity-75">{d.currentjob}</span>}
                            </div>
                            <h1 className="display-3 fw-bold mb-4">{d.name ? `Hi, I'm ${firstName}.` : "Welcome to my website"}</h1>
                            {d.slogan && <p className="lead text-light opacity-75 mb-5 pe-lg-5">{d.slogan}</p>}
                            <div className="d-flex gap-3 flex-wrap">
                                <a href="#portfolio" className="btn btn-primary btn-lg rounded-pill px-4 py-3 fw-bold shadow">View My Work</a>
                                <a href="#contact" className="btn btn-outline-light btn-lg rounded-pill px-4 py-3 fw-bold">Contact Me</a>
                            </div>
                        </div>
                        <div className="col-lg-5 text-center" data-aos="fade-left" data-aos-duration="800">
                            <div className={`hero-photo mx-auto ${hasPhoto ? "" : "hero-photo-ph"}`}>
                                {hasPhoto ? <img src={photo} alt={d.name} onError={() => setImgOk(false)} /> : <span>{initials}</span>}
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* 1. PORTFOLIO */}
            {images.length > 0 && (
                <section id="portfolio" className="py-5 mt-4">
                    <div className="container py-5">
                        <div className="text-center mb-5" data-aos="fade-up">
                            <span className="text-accent">Highlights</span>
                            <h2 className="display-5 section-title">Portfolio & Moments</h2>
                            <p className="text-muted mx-auto" style={{ maxWidth: 600 }}>A showcase of my work, events and community highlights.</p>
                        </div>
                        <div className="row g-4">
                            {images.map((img, i) => (
                                <div className="col-md-4" data-aos="fade-up" data-aos-delay={(i % 3 + 1) * 100} key={i}>
                                    <div className="card hover-lift bg-white shadow-sm h-100 border-0 rounded-4">
                                        <div className="portfolio-img-wrapper rounded-top-4">
                                            <img src={imgUrl(img.link)} alt={img.title || `Highlight ${i + 1}`} />
                                        </div>
                                        <div className="card-body p-4">
                                            {img.type && <span className="badge bg-light text-dark mb-2">{img.type}</span>}
                                            <h5 className="fw-bold mb-0">{img.title || "Highlight"}</h5>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* 2. ABOUT */}
            {d.aboutme && (
                <section id="about" className="py-5 bg-white">
                    <div className="container py-5">
                        <div className="row gx-lg-5 align-items-center">
                            <div className="col-lg-6 mb-4 mb-lg-0" data-aos="fade-right">
                                <div className={`about-photo ${hasPhoto ? "" : "about-photo-ph"}`}>
                                    {hasPhoto ? <img src={photo} alt={d.name} onError={() => setImgOk(false)} className="img-fluid rounded-4 shadow" /> : <span>{initials}</span>}
                                </div>
                            </div>
                            <div className="col-lg-6" data-aos="fade-left">
                                <span className="text-accent">About Me</span>
                                <h2 className="display-6 section-title mb-4">{d.name}</h2>
                                <p className="lead text-muted mb-4">{d.aboutme}</p>
                                <div className="row mt-4">
                                    <div className="col-sm-6 mb-3">
                                        <h3 className="fw-bold text-primary display-6 mb-0">{d.experience || "10+"}</h3>
                                        <span className="text-muted fw-semibold">Experience</span>
                                    </div>
                                    {langCount > 0 && (
                                        <div className="col-sm-6 mb-3">
                                            <h3 className="fw-bold text-primary display-6 mb-0">{langCount}</h3>
                                            <span className="text-muted fw-semibold">Languages Spoken</span>
                                        </div>
                                    )}
                                </div>
                                <div className="d-flex flex-wrap gap-2 mt-2">
                                    {d.addresses && <span className="chip"><i className="bi bi-geo-alt-fill me-1"></i>{d.addresses}</span>}
                                    {d.languages && <span className="chip"><i className="bi bi-translate me-1"></i>{d.languages}</span>}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* 3. WHAT I DO */}
            {SERVICES.length > 0 && (
                <section id="services" className="py-5">
                    <div className="container py-5">
                        <div className="text-center mb-5" data-aos="fade-up">
                            <span className="text-accent">What I Do</span>
                            <h2 className="display-5 section-title">My Focus Areas</h2>
                        </div>
                        <div className="row g-4">
                            {SERVICES.map((s, i) => (
                                <div className="col-md-4" data-aos="fade-up" data-aos-delay={(i + 1) * 100} key={i}>
                                    <div className="card bg-white border-0 shadow-sm p-4 h-100 hover-lift rounded-4">
                                        <div className={`bg-${s.tone} bg-opacity-10 text-${s.tone} p-3 rounded-3 d-inline-block mb-3`} style={{ width: "fit-content" }}>
                                            <i className={`bi ${s.icon} fs-3`}></i>
                                        </div>
                                        <h4 className="fw-bold mb-3">{s.t}</h4>
                                        <p className="text-muted mb-0">{s.d}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* 4. REVIEWS */}
            {TESTIMONIALS.length > 0 && (
                <section id="reviews" className="py-5 bg-white">
                    <div className="container py-5 border-top">
                        <div className="text-center mb-5" data-aos="fade-up">
                            <span className="text-accent">Testimonials</span>
                            <h2 className="display-5 section-title">What People Say</h2>
                        </div>
                        <div className="row g-4">
                            {TESTIMONIALS.map((t, i) => (
                                <div className="col-md-6" data-aos="fade-up" data-aos-delay={i * 100} key={i}>
                                    <div className="p-4 bg-light rounded-4 shadow-sm h-100">
                                        <div className="text-warning mb-3">
                                            <i className="bi bi-star-fill"></i><i className="bi bi-star-fill"></i><i className="bi bi-star-fill"></i><i className="bi bi-star-fill"></i><i className="bi bi-star-fill"></i>
                                        </div>
                                        <p className="fs-5 fst-italic">"{t.q}"</p>
                                        <h6 className="fw-bold mt-4 mb-0">{t.n}</h6>
                                        <small className="text-muted">{t.r}</small>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* 5. CONTACT */}
            <section id="contact" className="py-5 bg-white">
                <div className="container py-5 border-top">
                    <div className="row g-5">
                        <div className="col-lg-5" data-aos="fade-right">
                            <span className="text-accent">Get in Touch</span>
                            <h2 className="display-6 section-title mb-4">Contact Information</h2>
                            <p className="text-muted mb-5">Reach out to connect, collaborate or simply say hello. I usually respond within a few hours on business days.</p>
                            <ul className="list-unstyled mb-0">
                                {d.addresses && (
                                    <li className="d-flex mb-4 align-items-center">
                                        <div className="bg-light p-3 rounded-circle me-3 text-primary"><i className="bi bi-geo-alt-fill fs-5"></i></div>
                                        <div><h6 className="fw-bold mb-1">Location</h6><span className="text-muted">{d.addresses}</span></div>
                                    </li>
                                )}
                                {d.email && (
                                    <li className="d-flex mb-4 align-items-center">
                                        <div className="bg-light p-3 rounded-circle me-3 text-primary"><i className="bi bi-envelope-fill fs-5"></i></div>
                                        <div><h6 className="fw-bold mb-1">Email</h6><a href={`mailto:${d.email}`} className="text-muted text-decoration-none">{d.email}</a></div>
                                    </li>
                                )}
                                {d.mobile && (
                                    <li className="d-flex mb-4 align-items-center">
                                        <div className="bg-light p-3 rounded-circle me-3 text-primary"><i className="bi bi-telephone-fill fs-5"></i></div>
                                        <div><h6 className="fw-bold mb-1">Call</h6><a href={`tel:${d.mobile}`} className="text-muted text-decoration-none fw-bold">{d.mobile}</a></div>
                                    </li>
                                )}
                                {d.whatsapp && (
                                    <li className="d-flex align-items-center">
                                        <div className="bg-light p-3 rounded-circle me-3 text-success"><i className="bi bi-whatsapp fs-5"></i></div>
                                        <div><h6 className="fw-bold mb-1">WhatsApp</h6><a href={`https://wa.me/${d.whatsapp}`} target="_blank" rel="noreferrer" className="text-muted text-decoration-none fw-bold">{d.whatsapp}</a></div>
                                    </li>
                                )}
                            </ul>
                        </div>
                        <div className="col-lg-7" data-aos="fade-left">
                            <div className="card border-0 shadow-sm rounded-4 bg-light p-4 p-lg-5">
                                <h4 className="fw-bold mb-4">Send a Message</h4>
                                <form onSubmit={handleContact}>
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold">Full Name</label>
                                            <input type="text" className="form-control form-control-lg" value={cName} onChange={(e) => setCName(e.target.value)} required />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold">Phone Number</label>
                                            <input type="tel" className="form-control form-control-lg" value={cPhone} onChange={(e) => setCPhone(e.target.value)} required />
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label fw-semibold">Email Address</label>
                                            <input type="email" className="form-control form-control-lg" value={cEmail} onChange={(e) => setCEmail(e.target.value)} required />
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label fw-semibold">Message</label>
                                            <textarea className="form-control form-control-lg" rows="4" value={cMsg} onChange={(e) => setCMsg(e.target.value)} required></textarea>
                                        </div>
                                        <div className="col-12 mt-4">
                                            <button type="submit" className="btn btn-primary btn-lg w-100 fw-bold rounded-pill">Send Message</button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FOOTER */}
            <footer className="bg-dark text-light py-5 border-top border-secondary mt-5">
                <div className="container py-4">
                    <div className="row g-5">
                        <div className="col-lg-5 col-md-6">
                            <span className="text-white fw-bold fs-4 mb-3 d-block">
                                <i className="bi bi-patch-check-fill text-primary me-2"></i>{d.name || "My Website"}
                            </span>
                            <p className="text-secondary mb-4">{d.slogan || d.aboutme}</p>
                            <div className="d-flex gap-3 flex-wrap">
                                {d.mobile && <a href={`tel:${d.mobile}`} className="text-secondary text-decoration-none fs-5"><i className="bi bi-telephone-fill"></i></a>}
                                {d.whatsapp && <a href={`https://wa.me/${d.whatsapp}`} target="_blank" rel="noreferrer" className="text-secondary text-decoration-none fs-5"><i className="bi bi-whatsapp"></i></a>}
                                {d.email && <a href={`mailto:${d.email}`} className="text-secondary text-decoration-none fs-5"><i className="bi bi-envelope-fill"></i></a>}
                            </div>
                        </div>
                        <div className="col-lg-3 col-md-6">
                            <h5 className="fw-bold mb-4">Quick Links</h5>
                            <ul className="list-unstyled mb-0">
                                <li className="mb-2"><a href="#portfolio" className="text-secondary text-decoration-none">Portfolio</a></li>
                                <li className="mb-2"><a href="#about" className="text-secondary text-decoration-none">About</a></li>
                                <li className="mb-2"><a href="#services" className="text-secondary text-decoration-none">What I Do</a></li>
                                <li className="mb-2"><a href="#reviews" className="text-secondary text-decoration-none">Reviews</a></li>
                            </ul>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <h5 className="fw-bold mb-4">Focus Areas</h5>
                            <ul className="list-unstyled mb-0">
                                {SERVICES.map((s, i) => (
                                    <li className="mb-2" key={i}><a href="#services" className="text-secondary text-decoration-none">{s.t}</a></li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <hr className="border-secondary my-5" />
                    <div className="row align-items-center">
                        <div className="col-md-6 text-center text-md-start mb-3 mb-md-0">
                            <p className="text-secondary small mb-0">&copy; {new Date().getFullYear()} <span>{d.name || "My Website"}</span>. All rights reserved.</p>
                        </div>
                        <div className="col-md-6 text-center text-md-end">
                            <p className="text-secondary small mb-0">Powered by <span className="text-gold fw-semibold">Dial Nepal</span></p>
                        </div>
                    </div>
                </div>
            </footer>

            {/* THEME OVERRIDES (DialNepal blue + gold) */}
            <style>{`
                .pw{ font-family:'Inter',sans-serif; color:#333; background:#f8f9fa; }
                .pw h1,.pw h2,.pw h3,.pw h4,.pw .navbar-brand{ font-family:'Poppins',sans-serif; }
                .pw-loading{ display:flex; align-items:center; justify-content:center; min-height:100vh; background:#f8f9fa; }

                .pw .text-primary{ color:#2563eb !important; }
                .pw .btn-primary{ --bs-btn-bg:#2563eb; --bs-btn-border-color:#2563eb; --bs-btn-hover-bg:#1e40af; --bs-btn-hover-border-color:#1e40af; --bs-btn-active-bg:#1e3a8a; --bs-btn-active-border-color:#1e3a8a; }
                .pw .bg-primary{ background-color:#2563eb !important; }
                .pw .text-gold{ color:#f6b100 !important; }

                .pw .navbar{ background-color:rgba(255,255,255,.98) !important; backdrop-filter:blur(10px); border-bottom:1px solid #eaeaea; }
                .pw .nav-link{ color:#0f172a; } .pw .nav-link:hover{ color:#2563eb; }

                /* HERO: image background (dark overlay) + framed profile photo */
                .pw .hero-section{
                    background:linear-gradient(120deg, #0f172a 0%, #1e3a8a 55%, #2563eb 100%);
                    background-size:cover; background-position:center; background-repeat:no-repeat;
                    color:#fff; padding:150px 0 120px 0;
                }
                .pw .hero-photo{ width:300px; height:300px; border-radius:50%; overflow:hidden; border:6px solid rgba(255,255,255,.18); box-shadow:0 20px 45px rgba(0,0,0,.4); background:#e9edf6; }
                .pw .hero-photo img{ width:100%; height:100%; object-fit:cover; }
                .pw .hero-photo-ph{ display:flex; align-items:center; justify-content:center; background:linear-gradient(135deg,#f6b100,#e0a000); }
                .pw .hero-photo-ph span{ font-family:'Poppins',sans-serif; font-size:104px; font-weight:800; color:#0f172a; }
                @media(max-width:991px){ .pw .hero-photo{ width:230px; height:230px; } }

                .pw .section-title{ font-weight:800; color:#0f172a; margin-bottom:1rem; }
                .pw .text-accent{ color:#2563eb; font-weight:600; letter-spacing:1px; text-transform:uppercase; font-size:.85rem; }

                .pw .hover-lift{ transition:transform .3s ease, box-shadow .3s ease; }
                .pw .hover-lift:hover{ transform:translateY(-5px); box-shadow:0 15px 30px rgba(0,0,0,.1) !important; }

                .pw .portfolio-img-wrapper{ overflow:hidden; height:260px; }
                .pw .portfolio-img-wrapper img{ transition:transform .5s ease; width:100%; height:100%; object-fit:cover; }
                .pw .hover-lift:hover .portfolio-img-wrapper img{ transform:scale(1.08); }

                .pw .about-photo img{ width:100%; height:440px; object-fit:cover; }
                .pw .about-photo-ph{ height:440px; border-radius:1rem; display:flex; align-items:center; justify-content:center; background:linear-gradient(135deg,#2563eb,#1e40af); color:#fff; box-shadow:0 15px 35px rgba(0,0,0,.15); }
                .pw .about-photo-ph span{ font-family:'Poppins',sans-serif; font-size:120px; font-weight:800; }

                .pw .chip{ background:#eff6ff; color:#1d4ed8; font-weight:600; padding:8px 16px; border-radius:50px; display:inline-block; border:1px solid #bfdbfe; font-size:.9rem; }
            `}</style>
        </div>
    );
}
